/**
 * MainSlot — renders the currently active main view (from useLayoutStore)
 * wrapped in a consistent top-bar chrome: back button, title, expand/collapse
 * toggle. Returns null when no main view is active.
 *
 * See UI_TAXONOMY.md §4 for slot rules and §6 for MAIN_VIEWS registry.
 */

import { useState, useEffect, useRef } from 'react';
import type { AppState } from "@strands/agent-transport";
import { useLayoutStore } from './LayoutProvider';
import { MAIN_VIEWS } from './mainViews';
import { buildInfographicPdf, exportInfographicPdf } from '@/modules/chat/utils/exportInfographicPdf';
import { uploadFiles } from '@/modules/chat/api/files';
import { apiFetch } from '@/shared/api';

interface Props {
  /** Current AG-UI session state. Spread into the active view as `state` so
   * state-driven main views (e.g. CanvasView reading state.canvas) don't
   * need their own subscription. */
  state: AppState;
  /** Active session id — useful for views that need the session context. */
  sessionId: string | null;
  /** Local state mutator. Views owning client-side state keys (canvas draft
   * etc) call this; the new value ships to the server on the next
   * sendMessage via RunAgentInput.state (AG-UI idiom). */
  updateState: (key: string, value: unknown) => void;
}

export function MainSlot({ state, sessionId, updateState }: Props) {
  const { mainView, expandMain, collapseMain, closeMain } = useLayoutStore();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
  const pdfMenuRef = useRef<HTMLDivElement>(null);

  // Outside-click closes the PDF menu. Same idiom as MailComposeView's
  // attachment picker.
  useEffect(() => {
    if (!pdfMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (pdfMenuRef.current && !pdfMenuRef.current.contains(e.target as Node)) {
        setPdfMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pdfMenuOpen]);

  if (!mainView) return null;

  const entry = MAIN_VIEWS[mainView.id];
  if (!entry) {
    // Guarded in openMain too, but belt-and-braces in case registry changes.
    console.error(`[MainSlot] No entry for id: "${mainView.id}"`);
    return null;
  }

  const Component = entry.component;
  const modeClass = mainView.expanded ? 'main-slot--expanded' : 'main-slot--split';
  const showPdf = mainView.id === 'infographic';
  // Views that preview a single workspace file (carried as `props.path`) get
  // a download icon in the chrome — mirrors the infographic's download
  // affordance instead of an in-view button. The PDF viewer (presentations +
  // PDFs) downloads the ORIGINAL file via `/files/...`, same as the xlsx
  // preview. Docx keeps its own download in the editor toolbar, so it's
  // intentionally not listed here.
  const FILE_PREVIEW_VIEWS = new Set(['xlsxViewer', 'pptxViewer', 'pdfViewer']);
  const downloadPath = FILE_PREVIEW_VIEWS.has(mainView.id)
    ? (mainView.props as { path?: string } | undefined)?.path
    : undefined;
  // For the spreadsheet viewer, show the actual file name (basename) instead
  // of the generic registry title — read-only text, no rename affordance.
  const headerTitle = downloadPath ? (downloadPath.split('/').pop() ?? entry.title) : entry.title;

  const handleDownloadFile = async () => {
    if (!sessionId || !downloadPath) return;
    try {
      const resp = await apiFetch(`/api/sessions/${sessionId}/files/${encodeURI(downloadPath)}`);
      if (!resp.ok) return;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadPath.split('/').pop() ?? downloadPath;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      /* ignore — best-effort download */
    }
  };

  const handleDownloadPdf = async () => {
    if (pdfBusy) return;
    setPdfMenuOpen(false);
    setPdfBusy(true);
    // `exportInfographicPdf` forces the capture root to a fixed narrow
    // width temporarily, so no expand/collapse dance is needed. The
    // overlay (gated on `pdfBusy`) hides the brief layout shift.
    try {
      await exportInfographicPdf();
    } catch (err) {
      console.error('[MainSlot] PDF export failed', err);
    } finally {
      setPdfBusy(false);
    }
  };

  const handleSaveToWorkspace = async () => {
    if (pdfBusy || !sessionId) return;
    setPdfMenuOpen(false);
    setPdfBusy(true);
    try {
      const built = await buildInfographicPdf();
      if (!built) return;
      await uploadFiles(sessionId, [{ path: built.name, data: built.blob }]);
    } catch (err) {
      console.error('[MainSlot] PDF save-to-workspace failed', err);
    } finally {
      setPdfBusy(false);
    }
  };

  // Spread caller-provided props plus onClose so the view can dismiss itself.
  // Also pass `state`, `sessionId`, and `updateState` so state-driven views
  // (CanvasView etc) can read AG-UI session state and mutate client-owned
  // keys without their own subscription or REST calls.
  const viewProps = {
    ...((mainView.props ?? {}) as Record<string, unknown>),
    state,
    sessionId,
    updateState,
    onClose: closeMain,
  };

  return (
    <div className={`main-slot ${modeClass}`}>
      <header className="main-slot-header">
        {headerTitle && <span className="main-slot-title">{headerTitle}</span>}
        <div className="main-slot-actions">
          {showPdf && (
            <div className="main-slot-pdf-menu" ref={pdfMenuRef}>
              <button
                type="button"
                className="main-slot-action"
                onClick={() => setPdfMenuOpen((v) => !v)}
                disabled={pdfBusy}
                title="Сохранить PDF"
                aria-label="Сохранить PDF"
                aria-haspopup="menu"
                aria-expanded={pdfMenuOpen}
              >
                {pdfBusy ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="main-slot-spinner">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
              </button>
              {pdfMenuOpen && !pdfBusy && (
                <div className="main-slot-pdf-menu-dropdown" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="main-slot-pdf-menu-item"
                    onClick={handleDownloadPdf}
                  >
                    Скачать PDF
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="main-slot-pdf-menu-item"
                    onClick={handleSaveToWorkspace}
                    disabled={!sessionId}
                  >
                    Сохранить в воркспейс
                  </button>
                </div>
              )}
            </div>
          )}
          {downloadPath && (
            <button
              type="button"
              className="main-slot-action"
              onClick={() => void handleDownloadFile()}
              title="Скачать"
              aria-label="Скачать"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="main-slot-action"
            onClick={mainView.expanded ? collapseMain : expandMain}
            title={mainView.expanded ? 'Свернуть' : 'Развернуть на полный экран'}
            aria-label={mainView.expanded ? 'Свернуть' : 'Развернуть'}
          >
            {mainView.expanded ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 14h6v6" />
                <path d="M20 10h-6V4" />
                <path d="M14 10l7-7" />
                <path d="M3 21l7-7" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7 7" />
                <path d="M3 21l7-7" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="main-slot-action main-slot-close"
            onClick={closeMain}
            title="Закрыть"
            aria-label="Закрыть"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </header>
      <div className="main-slot-body">
        <Component {...viewProps} />
        {pdfBusy && (
          <div className="main-slot-pdf-overlay" aria-live="polite">
            <div className="main-slot-pdf-overlay-card">
              <svg className="main-slot-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>Готовлю PDF…</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
